import { useState } from 'react';
import { DEFAULT_MAX_RANGE_PASSENGER_COUNT, stopover } from 'remote/global';
import GoogleMapReact from 'google-map-react';
import { useMapsGoogleConfigurationQuery } from 'api/queries/config';
import { Box } from '@mui/material';
import { useForm, SubmitHandler } from 'react-hook-form';
import {
  CorporateCharterItinerarySchemaType,
  MINIMUM_STOPOVERS,
  corporateCharterItinerarySchema,
} from 'components/forms/CorporateCharter/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { getGeocodedValuesByLatLng } from 'utils/helpers';
import LocationMarker from './LocationMarker';
import StopoverForm from './StopoverForm';

const defaultProps = {
  center: {
    lat: 59.41,
    lng: 24.77,
  },
  zoom: 11,
};

interface Props {
  onValid: SubmitHandler<CorporateCharterItinerarySchemaType>;
  defaultValues?: CorporateCharterItinerarySchemaType;
}

function CreateItinerary({ defaultValues, onValid }: Props) {
  const { data: googleConfig } = useMapsGoogleConfigurationQuery();
  const [isMapDraggable, setIsMapDraggable] = useState<boolean>(true);
  const [selectedRowIndex, setSelectedRowIndex] = useState<number>(0);
  const formInstance = useForm<CorporateCharterItinerarySchemaType>({
    defaultValues: {
      passengerCount: '' as any,
      maxPassengerCount: DEFAULT_MAX_RANGE_PASSENGER_COUNT,
      departures: [
        {
          clientDepartureTime: '',
        },
      ],
      stops: Array.from({ length: MINIMUM_STOPOVERS }).map(() => stopover),
      ...defaultValues,
    },
    resolver: zodResolver(corporateCharterItinerarySchema),
  });

  const { setValue, watch, clearErrors } = formInstance;
  const stopsWatch = watch('stops');

  return !googleConfig ? null : (
    <Box
      sx={{
        display: {
          xs: 'block',
          md: 'flex',
        },
        flexDirection: 'row',
        pb: '24px',
        height: {
          xs: 'inherit',
          md: '1000px',
        },
      }}
    >
      <Box flex={1} mr='24px' ml='24px'>
        <StopoverForm
          formInstance={formInstance}
          onValid={onValid}
          selectedRow={selectedRowIndex}
          setSelectedRow={setSelectedRowIndex}
          googleConf={googleConfig}
        />
      </Box>
      <Box
        flex={1.5}
        sx={{
          height: {
            xs: '1000px',
            md: '100%',
          },
          mt: {
            xs: '24px',
            md: '0',
          },
        }}
      >
        <GoogleMapReact
          bootstrapURLKeys={{
            key: googleConfig?.feApiKey || '',
          }}
          onChildMouseDown={(key) => {
            const index = stopsWatch.findIndex(({ placeId }: any) => placeId === key);
            setSelectedRowIndex(index);
            setIsMapDraggable(false);
          }}
          onChildMouseMove={(key, _, { lat, lng }) => {
            const index = stopsWatch.findIndex(({ placeId }: any) => placeId === key);
            if (index === -1) return;
            setValue(`stops.${index}.coords`, {
              lat,
              lon: lng,
            });
          }}
          onChildMouseUp={async (key) => {
            const index = stopsWatch.findIndex(({ placeId }: any) => placeId === key);
            if (index === -1) return;

            const { coords } = stopsWatch[index];
            if (!coords) return;

            const { lat, lon }: { lat: number; lon: number } = coords;

            const parsedValues = await getGeocodedValuesByLatLng(lat, lon);
            if (!parsedValues) return;

            setValue(`stops.${index}`, parsedValues);
            setIsMapDraggable(true);
          }}
          draggable={isMapDraggable}
          layerTypes={['TrafficLayer', 'TransitLayer']}
          onClick={async (val) => {
            const { lat, lng } = val;
            clearErrors(`stops.${selectedRowIndex}`);
            const parsedValues = await getGeocodedValuesByLatLng(lat, lng);
            setValue(`stops.${selectedRowIndex}`, {
              ...stopsWatch[selectedRowIndex],
              ...(parsedValues as any),
            });
            if (selectedRowIndex === stopsWatch.length - 1) return;
            setSelectedRowIndex(selectedRowIndex + 1);
          }}
          yesIWantToUseGoogleMapApiInternals
          defaultCenter={defaultProps.center}
          defaultZoom={defaultProps.zoom}
        >
          {stopsWatch.map((stop, index: number) => {
            return (
              stop?.coords?.lat && (
                <LocationMarker
                  selected={selectedRowIndex === index}
                  text={(index + 1).toString()}
                  key={stop.placeId}
                  lat={stop.coords.lat}
                  lng={stop.coords.lon}
                />
              )
            );
          })}
        </GoogleMapReact>
      </Box>
    </Box>
  );
}

export default CreateItinerary;
